import { AxiosResponse } from "axios";
import axios, { configHeader, getHeaders } from "./axios";
import { MenuResponseByID } from "./menu.api";
import { uniqBy } from "lodash";

export interface OrderingBody {
  menu_id: number;
  type: string;
  sweetness: number;
  remark: string;
  tumblerImage: string;
}

export type AllOrderResponse = OrderResponse[];

export interface OrderResponse {
  data: any;
  user: UserResponse;
  menu: MenuResponseByID;
  type: string;
  sweetness: number;
  remark: string;
  tumblerImage: string;
  updatedAt: string;
  rejectNote: any;
  id: number;
  status: string;
  orderDate: string;
  message: string;
  baristaOnBreak?: boolean;
  breakNotice?: string | null;
}

export interface UserResponse {
  id?: number;
  sub?: string;
  email?: string;
  name?: string;
  role?: string;
}

export const postOrder = async (body: OrderingBody): Promise<OrderResponse> => {
  const headers = configHeader();
  const orderSucces = await axios.post<OrderResponse>(`/order`, body, headers);
  return orderSucces.data;
};

export const getOrderByStatus = async (
  status: string,
  startDate?: string,
  endDate?: string,
  categoryFor?: "cafe" | "dessert",
  limit = 500,
): Promise<AllOrderResponse> => {
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<AllOrderResponse> = await axios.get("/order", {
    params: {
      status,
      startDate,
      endDate,
      categoryFor,
      limit: String(limit),
    },
    headers: {
      Authorization: "Bearer " + accessToken,
    },
  });
  return response.data;
};

export const getAllOrderByStatus = async (): Promise<AllOrderResponse> => {
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<AllOrderResponse> = await axios.get("/order", {
    headers: {
      Authorization: "Bearer " + accessToken,
    },
  });
  return response.data;
};

/** Server-side max page size for GET /order. */
export const ORDER_PAGE_SIZE = 5000;
/** Safety cap so a misbehaving server can't make us loop forever. */
export const ORDER_MAX_PAGES = 40;

/**
 * Fetch every order in a date range, no status filter. Used by dashboards.
 * Pages with limit/offset until a short page comes back (or the safety cap
 * is reached) so large ranges aren't silently truncated at one page.
 */
export const getOrdersInRange = async (
  startDate: string,
  endDate: string,
  limit = ORDER_PAGE_SIZE,
): Promise<AllOrderResponse> => {
  const pageSize = Math.min(Math.max(1, limit), ORDER_PAGE_SIZE);
  const accessToken = window.localStorage.getItem("access_token");
  const all: AllOrderResponse = [];
  for (let page = 0; page < ORDER_MAX_PAGES; page++) {
    const response: AxiosResponse<AllOrderResponse> = await axios.get("/order", {
      params: {
        startDate,
        endDate,
        limit: String(pageSize),
        offset: String(page * pageSize),
      },
      headers: { Authorization: "Bearer " + accessToken },
    });
    const rows = Array.isArray(response.data) ? response.data : [];
    all.push(...rows);
    if (rows.length < pageSize) break;
  }
  return all;
};

export const getOrderByID = async (id: number): Promise<OrderResponse> => {
  const headers = getHeaders();
  const response = await axios.get(`/order/${id}`, headers);
  return response.data;
};

export interface CountTotalResponse {
  totalOrder: number;
  /** Present from /order/count/user. */
  count?: number;
  /** Present from /order/count/user — effective daily limit (per-user
   *  override > global app setting > legacy fallback of 2). */
  limit?: number;
}

export const getCountTotalAll = async (
  startDate: string,
  endDate: string,
  categoryFor: string
): Promise<CountTotalResponse> => {
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<CountTotalResponse> = await axios.get(
    "/order/count/total",
    {
      params: {
        startDate: startDate,
        endDate: endDate,
        categoryFor: categoryFor,
      },
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return response.data;
};

export const getPopularMenuAll = async (
  startDate: string,
  endDate: string
): Promise<AllOrderResponse> => {
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<AllOrderResponse> = await axios.get(
    "/order/find/popular-menu",
    {
      params: {
        startDate: startDate,
        endDate: endDate,
      },
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return response.data;
};

export const getCountInOneDay = async (): Promise<CountTotalResponse> => {
  const headers = getHeaders();
  const response: AxiosResponse<CountTotalResponse> = await axios.get(
    "/order/count/user",
    headers
  );
  return response.data;
};

export const getHistoryUser = async (
  status?: string,
  startDate?: string,
  endDate?: string
): Promise<AllOrderResponse> => {
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<AllOrderResponse> = await axios.get(
    "/order/history/user",
    {
      params: {
        status: status,
        startDate: startDate,
        endDate: endDate,
      },
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return uniqBy(response.data, 'id');
};

export const getAllHistoryUser = async (): Promise<AllOrderResponse> => {
  const date = new Date();
  const accessToken = window.localStorage.getItem("access_token");
  const response: AxiosResponse<AllOrderResponse> = await axios.get(
    "/order/history/user",
    {
      params: {
        startDate: date,
        endDate: date,
      },
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    }
  );
  return uniqBy(response.data, 'id');
};

export interface patchOrderBody {
  type?: string;
  sweetness?: number;
  remark?: string;
  tumblerImage?: string;
  status?: string;
  rejectNote?: string;
}

export interface OrderByIDResponse {
  id?: number;
  status?: string;
  orderDate?: string;
  sweetness?: number;
  rejectNote?: string;
  remark?: string;
  tumblerImage?: string;
  type?: string;
}

export const patchOrderByID = async (
  id: number,
  body: patchOrderBody
): Promise<OrderByIDResponse> => {
  const headers = getHeaders();
  // API v2 split state-machine transitions out of PATCH for security.
  // Map legacy status changes to dedicated endpoints; pass everything else through.
  if (body.status === "cancel") {
    const r = await axios.post<OrderByIDResponse>(`/order/${id}/cancel`, {}, headers);
    return r.data;
  }
  if (body.status === "picked") {
    const r = await axios.post<OrderByIDResponse>(`/order/${id}/pickup`, {}, headers);
    return r.data;
  }
  const { status, rejectNote, ...rest } = body;
  const response = await axios.patch<OrderByIDResponse>(`/order/${id}`, rest, headers);
  return response.data;
};
export interface OrderBaristaBody {
  reject_note: string;
  status: string;
}

export const patchOrderBaristaByID = async (
  id: number,
  body: OrderBaristaBody
): Promise<OrderByIDResponse> => {
  const headers = getHeaders();
  const response = await axios.patch<OrderByIDResponse>(
    `/order/barista/${id}`,
    body,
    headers
  );
  return response.data;
};

export const getQueue = async () => {
  const headers = getHeaders();
  const response = await axios.get(`/order/my-queue`, headers);
  return response.data;
};
